import type { JSX } from "react";
import { useTranslation } from "react-i18next";

import { MarketplaceChip } from "@/shared/components/MarketplaceChip";
import { Badge } from "@/shared/ui/badge";

import { money } from "../lib/format";
import { returnIntent, returnStatusLabel } from "../lib/status";
import type { ReturnRec } from "../lib/types";

interface ReturnDetailProps {
  returnRec: ReturnRec;
}

export function ReturnDetail({ returnRec: r }: ReturnDetailProps): JSX.Element {
  const { t } = useTranslation("orders");

  return (
    <div className="px-5 py-4 space-y-6">
      {/* Meta grid */}
      <div className="grid grid-cols-2 gap-x-6 gap-y-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] mb-0.5">
            {t("rd_order")}
          </div>
          <div className="font-mono text-[13px] font-semibold text-[var(--text-primary)]">
            {r.order}
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] mb-0.5">
            {t("rd_return")}
          </div>
          <div className="font-mono text-[13px] font-semibold text-[var(--text-primary)]">
            {r.ret}
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] mb-0.5">
            {t("lbl_marketplace")}
          </div>
          <div>
            <MarketplaceChip marketplace={r.mkp} />
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] mb-0.5">
            {t("rd_seller")}
          </div>
          <div className="text-sm text-[var(--text-primary)]">{r.seller}</div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] mb-0.5">
            {t("ret_shop_id")}
          </div>
          <div className="font-mono text-[13px] text-[var(--text-primary)]">
            {r.shopId || <span className="text-[var(--text-subtle)]">—</span>}
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] mb-0.5">
            {t("rd_amount")}
          </div>
          <div className="font-mono text-[13px] font-bold text-[var(--text-primary)]">
            {money(r.refund)}
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] mb-0.5">
            {t("th_return_status")}
          </div>
          <div>
            <Badge intent={returnIntent(r.status)} dot>
              {returnStatusLabel(r.status, t)}
            </Badge>
          </div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] mb-0.5">
            {t("rd_reason")}
          </div>
          <div className="text-sm text-[var(--text-primary)]">{r.reason}</div>
        </div>

        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] mb-0.5">
            {t("rd_tracking")}
          </div>
          <div className="font-mono text-[13px] text-[var(--text-primary)]">
            {r.tracking ?? <span className="text-[var(--text-subtle)]">—</span>}
          </div>
        </div>
      </div>

      {/* Return items section */}
      <div>
        <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--text-subtle)]">
          {t("sec_return_items")}
        </div>
        <div className="overflow-x-auto rounded-lg border border-[var(--premium-border)]">
          <table className="min-w-full border-collapse">
            <thead>
              <tr className="border-b border-[var(--row-divider)] bg-[var(--surface-sunken)]">
                <th className="h-8 px-3 text-left text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
                  {t("th_sku")}
                </th>
                <th className="h-8 px-3 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
                  {t("th_qty")}
                </th>
                <th className="h-8 px-3 text-right text-[11px] font-bold uppercase tracking-[0.07em] text-[var(--text-subtle)] whitespace-nowrap">
                  {t("th_unit")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[var(--row-divider)]">
              {r.items.map((item) => (
                <tr key={item.sku} className="hover:bg-[var(--row-hover)]">
                  <td className="h-8 px-3 whitespace-nowrap">
                    <span className="font-mono text-[12px] font-semibold text-[var(--text-primary)]">
                      {item.sku}
                    </span>
                  </td>
                  <td className="h-8 px-3 text-right text-sm tabular-nums text-[var(--text-primary)] whitespace-nowrap">
                    {item.q}
                  </td>
                  <td className="h-8 px-3 text-right text-sm tabular-nums text-[var(--text-primary)] whitespace-nowrap">
                    {money(item.price)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Stock effect note */}
      <div className="rounded-lg bg-[var(--surface-sunken)] px-3 py-2.5 text-xs text-[var(--text-subtle)]">
        {t("return_stock_note")}
      </div>
    </div>
  );
}
